import {
  CardEmployerList,
  CardEmployerListItem,
  CardIssueConfigResponse,
  CardIssueConfig,
  PartnerCardIssueCriteriaState,
  IssueCardCustomizationResponse,
  EmployerCardIssueCriteriaResponse,
  AccountHolderIssueCardCriteria,
  AccountHolderIssueCardCriteriaResponse,
  SetCardCriteriaCustomizationRequest,
  CreateCardIssueCriteriaRequest,
  CardIssueCustomizationOptionUpdateRequest,
  PlasticRushCodeModel,
  PlasticRushCodeValue
} from "@/admin/types";
import * as CardService from "@/shared/api/cardService";
import * as storeHelper from "@/shared/storeHelper";
import { FetchState } from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";
import moment from "moment";
import Vue from "vue";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: PartnerCardIssueCriteriaState = {
  partnerId: null,
  fetchState: FetchState.Uninitialized,
  cardIssueConfigs: {
    partnerId: 0,
    partner: null,
    employers: [],
    unsyncedUsers: [],
    syncedUsers: []
  },
  employersFetchState: FetchState.Uninitialized,
  employers: [],
  unconfiguredEmployers: [],
  cardIssueCustomizations: {},
  employerCardIssueCriterias: {},
  accountHoldersIssueCardCriterias: {},
  cardIssueCustomizationOptionsFetchState: FetchState.Uninitialized,
  cardIssueCustomizationOptions: []
};

export const usePartnerCardIssueCriteriaStore = defineStore("partnerCardIssueCriteria", {
  state: () => (_.cloneDeep(initialState)),
  getters: {
    isLoaded: state => state.fetchState === FetchState.Loaded,
    isEmployersLoaded: state => state.employersFetchState === FetchState.Loaded,
    allEmployers (): CardEmployerListItem[] {
      return this.isEmployersLoaded
        ? Object.keys(this.employers).map(key => this.employers[key])
        : [];
    },
    isCardIssueCustomizationLoaded: state => (issueCardCriteriaId: number) => {
      const item = state.cardIssueCustomizations[issueCardCriteriaId];
      return item && item.fetchState === FetchState.Loaded;
    },
    cardIssueCustomization: state => (issueCardCriteriaId: number) => {
        const item = state.cardIssueCustomizations[issueCardCriteriaId];
        return item ? item.customization : null;
    },
    isCardIssueCriteriaLoadedForEmployer: state => (employerId: number) => {
      const item = state.employerCardIssueCriterias[employerId];
      return item && item.fetchState === FetchState.Loaded;
    },
    cardIssueCriteriaForEmployer: state => (employerId: number) => {
        const item = state.employerCardIssueCriterias[employerId];
        if (!item || !item.cardIssueCriteria) {
          return null;
        }
        return item.cardIssueCriteria;
    },
    isCardIssueCriteriaLoadedForAccountHolders: state => (employerId: number) => {
      const item = state.accountHoldersIssueCardCriterias[employerId];
      return item && item.fetchState === FetchState.Loaded;
    },
    cardIssueCriteriaForAccountHolders: state => (employerId: number) => {
        const item = state.accountHoldersIssueCardCriterias[employerId];
        if (!item || !item.data) {
          return null;
        }
        return item.data;
    },
    canActivateCardIssuance: state => (issueCardCriteriaId: number): boolean => {
      const item = state.cardIssueCustomizations[issueCardCriteriaId];

      if (item && item.customization && item.customization !== null) {
          return true;
      }

      return false;
    }
  },

  actions: {
    setPartnerId (partnerId: number) {
      if (this.partnerId === partnerId) {
        return;
      }

      this.fetchState = FetchState.Uninitialized;
      this.employersFetchState = FetchState.Uninitialized;
      this.partnerId = partnerId;
      this.cardIssueConfigs = {
        partnerId: 0,
        partner: null,
        employers: [],
        unsyncedUsers: [],
        syncedUsers: []
      };
      this.employers = null;
    },
    setCardIssueCustomization (issueCardCriteriaId: number, customization: IssueCardCustomizationResponse) {
      if (!this.cardIssueCustomizations[issueCardCriteriaId]) {
        Vue.set(this.cardIssueCustomizations, issueCardCriteriaId, { customization });
      }

      if (customization) {
        this.cardIssueCustomizations[issueCardCriteriaId].customization = customization;
      }
    },
    setCardIssueCustomizationFetchState (issueCardCriteriaId: number, fetchState: FetchState ) {
      if (!this.cardIssueCustomizations[issueCardCriteriaId]) {
        Vue.set(this.cardIssueCustomizations, issueCardCriteriaId, { fetchState: FetchState.Uninitialized });
      }
      this.cardIssueCustomizations[issueCardCriteriaId].fetchState = fetchState;
    },
    setEmployerCardIssueCriteria (employerId: number, cardIssueCriteria: EmployerCardIssueCriteriaResponse) {
      if (!this.employerCardIssueCriterias[employerId]) {
        Vue.set(this.employerCardIssueCriterias, employerId, { cardIssueCriteria });
      }

      this.employerCardIssueCriterias[employerId].cardIssueCriteria = cardIssueCriteria;
    },
    setEmployerCardIssueCriteriaFetchState (employerId: number, fetchState: FetchState ) {
      if (!this.employerCardIssueCriterias[employerId]) {
        Vue.set(this.employerCardIssueCriterias, employerId, { fetchState: FetchState.Uninitialized });
      }
      this.employerCardIssueCriterias[employerId].fetchState = fetchState;
    },
    setAccountHoldersIssueCardCriterias (employerId: number, data: AccountHolderIssueCardCriteriaResponse) {
      if (!this.accountHoldersIssueCardCriterias[employerId]) {
        Vue.set(this.accountHoldersIssueCardCriterias, employerId, { data });
      }

      this.accountHoldersIssueCardCriterias[employerId].data = data;
    },
    setAccountHoldersIssueCardCriteriasFetchState (employerId: number, fetchState: FetchState ) {
      if (!this.accountHoldersIssueCardCriterias[employerId]) {
        Vue.set(this.accountHoldersIssueCardCriterias, employerId, { fetchState: FetchState.Uninitialized });
      }
      this.accountHoldersIssueCardCriterias[employerId].fetchState = fetchState;
    },
    setLoading () {
      this.fetchState = FetchState.Loading;
    },
    setEmployersLoading () {
      this.employersFetchState = FetchState.Loading;
    },
    setUninitialized () {
      this.fetchState = FetchState.Uninitialized;
    },
    setEmployersUnitialized () {
      this.fetchState = FetchState.Uninitialized;
    },
    getConfigStatus (config: CardIssueConfig | AccountHolderIssueCardCriteria) {
      if (config === null) return "";
      if (config.dateEnabled === null) return "Inactive";
      if (moment().endOf("day").isAfter(moment(config.dateEnabled))) return "Active";
      return "Pending";
    },
    setCardIssueConfigs (dto: CardIssueConfigResponse) {
      if (this.partnerId !== dto.partnerId) {
        return;
      }

      this.cardIssueConfigs = dto;
      this.fetchState = FetchState.Loaded;
    },
    setEmployers (dto: CardEmployerList) {
      const newMap = dto.employers.reduce(
        (map, listItem) => {
          map[listItem.employerId] = listItem;
          return map;
        },
        {});
      this.employers = newMap;
      this.employersFetchState = FetchState.Loaded;
    },
    setUnconfiguredEmployers (dto: CardEmployerList) {
      this.unconfiguredEmployers = dto.employers;
    },
    getEmployerName (employerId: number): string {
      const er = this.employers ? this.employers[employerId] : null;
      if (this.isEmployersLoaded && er) { return er.name; }
      return "";
    },
    reloadPartnerCardIssueStore (): Promise<any> {
      this.setUninitialized();
      return this.loadPartnerCardIssueStore(this.partnerId);
    },
    loadPartnerCardIssueStore (partnerId: number): Promise<any> {
      if (this.partnerId === partnerId &&
        this.fetchState !== FetchState.Uninitialized &&
        this.employersFetchState !== FetchState.Uninitialized) {

          return Promise.resolve();
      }

      this.setPartnerId(partnerId);

      return Promise.allSettled([
        this.loadEmployers(partnerId),
        this.loadCardIssueConfigs(partnerId),
        this.loadUnconfiguredEmployers(partnerId)
      ]);
    },
    reloadEmployerCardIssueCriteria (employerId: number) {
      this.setEmployerCardIssueCriteriaFetchState(employerId, FetchState.Uninitialized);
      this.loadEmployerCardIssueCriteria(employerId);
    },
    loadEmployerCardIssueCriteria (employerId: number): Promise<any> {
      if (this.employerCardIssueCriterias && this.employerCardIssueCriterias[employerId] && this.employerCardIssueCriterias[employerId].fetchState !== FetchState.Uninitialized) {
        return Promise.resolve();
      }

      this.setEmployerCardIssueCriteriaFetchState(employerId, FetchState.Loading);

      return CardService.getEmployerIssueCriteria(employerId)
        .then(response => {
          this.setEmployerCardIssueCriteria(employerId, response.data);
          this.setEmployerCardIssueCriteriaFetchState(employerId, FetchState.Loaded);
        })
        .catch(error => {
          return storeHelper.handleError({error, rethrow: true});
        });
    },
    reloadAccountHoldersIssueCardCriterias (employerId: number) {
      this.setAccountHoldersIssueCardCriteriasFetchState(employerId, FetchState.Uninitialized);
      this.loadAccountHoldersIssueCardCriterias(employerId);
    },
    loadAccountHoldersIssueCardCriterias (employerId: number): Promise<any> {
      if (this.accountHoldersIssueCardCriterias && this.accountHoldersIssueCardCriterias[employerId] && this.accountHoldersIssueCardCriterias[employerId].fetchState !== FetchState.Uninitialized) {
        return Promise.resolve();
      }

      this.setAccountHoldersIssueCardCriteriasFetchState(employerId, FetchState.Loading);

      return CardService.getAccountHoldersIssueCardCriterias(employerId)
        .then(response => {
          this.setAccountHoldersIssueCardCriterias(employerId, response.data);
          this.setAccountHoldersIssueCardCriteriasFetchState(employerId, FetchState.Loaded);
        })
        .catch(error => {
          return storeHelper.handleError({error, rethrow: true});
        });
    },
    loadEmployers (partnerId: number): Promise<any> {
      if (this.employersFetchState !== FetchState.Uninitialized && this.partnerId === partnerId) {
        return Promise.resolve();
      }

      this.setEmployersLoading();
      return CardService.getEmployersForPartner(partnerId)
        .then(response => {
          this.setEmployers(response.data);
        })
        .catch(error => {
          this.setEmployersUninitialized();
          return storeHelper.handleError({error, rethrow: true});
        });
    },
    loadUnconfiguredEmployers (partnerId: number): Promise<any> {
      return CardService.getUnconfiguredEmployers(partnerId)
        .then(response => {
          this.setUnconfiguredEmployers(response.data);
        })
        .catch(error => {
          this.setUninitialized();
          return storeHelper.handleError({error, rethrow: true});
        });
    },
    loadCardIssueConfigs (partnerId: number): Promise<any> {
      if (this.fetchState !== FetchState.Uninitialized && this.partnerId === partnerId) {
        return Promise.resolve();
      }

      this.setLoading();
      return CardService.getPartnerIssueCriterias(partnerId)
        .then(response => {
          this.setCardIssueConfigs(response.data);
        })
        .catch(error => {
          this.setUninitialized();
          return storeHelper.handleError({error, rethrow: true});
        });
    },
    deleteCardIssueCriteria (issueCardCriteriaId: number) {
      return CardService.deleteCardIssueCriterias(issueCardCriteriaId)
      .catch(error => {
        return storeHelper.handleError({error, rethrow: true});
      });
    },
    createCardIssueCriteria (request: CreateCardIssueCriteriaRequest) {
      return CardService.createCardIssueCriteria(request)
      .catch(error => {
        return storeHelper.handleError({error, rethrow: true});
      });
    },
    activateCardIssueCriteria (issueCardCriteriaId: number) {
      return CardService.activateCardIssueCriterias(issueCardCriteriaId)
      .catch(error => {
        return storeHelper.handleError({error, rethrow: true});
      });
    },
    setCardIssueCriteriaCustomization (issueCardCriteriaId: number, request: SetCardCriteriaCustomizationRequest) {
      return CardService.setCardCriteriaCustomization(issueCardCriteriaId, request)
        .then(response => {
          this.reloadCardIssueCustomization(issueCardCriteriaId);
          return this.reloadPartnerCardIssueStore();
        })
        .catch(error => {
          return storeHelper.handleError({ error, rethrow: true });
      });
    },
    reloadCardIssueCustomization (issueCardCriteriaId: number) {
      this.setCardIssueCustomizationFetchState(issueCardCriteriaId, FetchState.Uninitialized);
      this.loadCardIssueCustomization(issueCardCriteriaId);
    },
    loadCardIssueCustomization (issueCardCriteriaId: number): Promise<any> {
      if (this.cardIssueCustomizations && this.cardIssueCustomizations[issueCardCriteriaId] && this.cardIssueCustomizations[issueCardCriteriaId].fetchState !== FetchState.Uninitialized) {
        return Promise.resolve();
      }

      this.setCardIssueCustomizationFetchState(issueCardCriteriaId, FetchState.Loading);

      return CardService.getCardIssueCustomization(issueCardCriteriaId)
        .then(response => {
          this.setCardIssueCustomization(issueCardCriteriaId, response.data);
          this.setCardIssueCustomizationFetchState(issueCardCriteriaId, FetchState.Loaded);
        })
        .catch(error => {
          return storeHelper.handleError({error, rethrow: true});
        });
    },
    loadCardIssueCustomizationOptions () {
      if (this.cardIssueCustomizationOptionsFetchState !== FetchState.Uninitialized) {
        return;
      }

      this.cardIssueCustomizationOptionsFetchState = FetchState.Loading;

      CardService.getCardIssueCustomizationOptions()
        .then(response => {
          this.cardIssueCustomizationOptions = response.data.customizationOptions;
          this.cardIssueCustomizationOptionsFetchState = FetchState.Loaded;
        })
        .catch(error => {
          this.setUninitialized();
          storeHelper.handleError({ error, rethrow: true });
        });
    },
    updateCardIssueCustomizationOptions (request: CardIssueCustomizationOptionUpdateRequest) {
      return CardService.updateCardIssueCustomizationOptions(request)
        .then(response => {
          this.cardIssueCustomizationOptions = response.data.customizationOptions;
          this.cardIssueCustomizationOptionsFetchState = FetchState.Loaded;

          const notifierStore = useNotifierStore();
          notifierStore.setSuccess("Card setting options successfully updated.");
        })
        .catch(error => storeHelper.handleError({ error, rethrow: true }));
    }
  }});
